<template lang="pug">
    .ma-5.simulation-results-container
        v-row.mb-5.d-flex.flex-row.justify-start.width-100-percent.mx-2
            v-col(cols="3")
                .text-uppercase.text-small Campaign
                .text-black {{ campaignName }}
            v-col(cols="3" v-if="!isCampaignSimulation")
                .text-uppercase.text-small {{ isProspectSimulation ? 'Prospect' : 'Customer' }}
                .text-black {{isProspectSimulation ? customer.first_name + ' ' + customer.last_name : customer.customer.full_name }}
            v-col(cols="3")
                .text-uppercase.text-small Organization
                .text-black {{ organizationName }}
            v-col(cols="3")
                .text-uppercase.text-small Simulation Datetime
                .text-black {{ date }} {{ time }}
        hr.mb-10

        div.text-center(v-if="isLoading")
            span.text-ih7.loading-text Creating Enroller Run Simulation...

        template(v-else)
            //- Query Parameters
            TimelineItem(
                v-if="queryParamsInProgress || queryParamsCompleted || queryParamsError"
                :inProgress="queryParamsInProgress"
                :completed="queryParamsCompleted"
                :error="queryParamsError"
                :errorMessage="queryParamsErrorMessage(enrollerRun.error_message)"
                title="Query Parameters"
                loadingText="Running customers against query parameters..."
            )
                template(v-slot:timeline-body)
                    //- Loading skeletons
                    div.event-container(
                        v-if="criterionsLoading && totalCriterions > 0"
                        v-for='(i, idx) in totalCriterions'
                        )
                        v-card.event-summary(elevation="0")
                            v-card-title.d-flex.flex-row.justify-space-between.align-start
                                v-skeleton-loader(type="text" width="80%")
                                v-skeleton-loader(type="text" width="80px")
                            v-card-text.d-flex.flex-row.justify-space-between
                                v-col(cols="4")
                                    v-skeleton-loader(type="text" width="80px")
                                    v-skeleton-loader(type="text" width="60px")
                                v-col(cols="4")
                                    v-skeleton-loader(type="text" width="100px")
                                    v-skeleton-loader(type="text" width="40px")
                                v-col(cols="4")
                                    v-skeleton-loader(type="text" width="140px")
                                    v-skeleton-loader(type="text" width="80px")
                    //- Query params results
                    div.event-container(v-if="enrollerRunCriterions.length > 0 && !criterionsLoading")
                        v-card.event-summary(
                            v-for='(enrollerRunCriterion, idx) in enrollerRunCriterions'
                            :key='idx'
                            elevation="0"
                        )
                            v-card-title.text-medium.d-flex.flex-row.justify-space-between.align-start.flex-nowrap.mx-2(style="gap: 20px;")
                                div.d-flex.flex-row
                                    span {{ enrollerRunCriterion.process_order }}.&nbsp;
                                    span(v-html="enrollerRunCriterion.mergeable_description")
                                span.text-uppercase.font-weight-bold.flex-shrink-0(:class="enrollerRunCriterion.status | statusClassMatcher") {{ enrollerRunCriterion.status }}
                            v-card-text.ma-5(v-if="enrollerRunCriterion.status!== 'canceled'")
                                v-col(cols='12').pt-0
                                    v-row.pt-0.d-flex.flex-row.align-center
                                        v-icon.mx-2(color="var(--volie-teal)") mdi-account-group
                                        span.text-uppercase.font-weight-bold(style="color: var(--volie-teal)") {{ enrollerRunCriterion.total_customers | number }} customer{{ handlePlural(enrollerRunCriterion.total_customers) }}
                                    v-row
                                        v-col(cols="4")
                                            .text-uppercase.text-small Duration: 
                                            .text-black {{ enrollerRunCriterion.duration | friendlyMillisecondFormat }}
                                        v-col(cols="4")
                                            .text-uppercase.text-small Query Attempts: 
                                            .text-black {{ enrollerRunCriterion.query_attempt_count }}
                                        v-col(cols="4")
                                            .text-uppercase.text-small Average Potential Duration: 
                                            .text-black {{ enrollerRunCriterion.average_potential_duration | friendlyMillisecondFormat }}

            //- Potentials
            TimelineItem(
                v-if="potentialsInProgress || potentialsCompleted || potentialsError"
                :inProgress="potentialsInProgress"
                :completed="potentialsCompleted"
                :error="potentialsError"
                :errorMessage="enrollerRun.error_message"
                title="Generate Potentials"
                loadingText="Generating potential customers..."
            )
                template(v-slot:timeline-body)
                    div.event-container
                        v-card.event-summary(
                            elevation="0"
                        )
                            v-card-title.text-medium
                                span(v-if="isCampaignSimulation")
                                    | {{ enrollerRun.total_enroller_run_customers | number }} potential customer{{ handlePlural(enrollerRun.total_enroller_run_customers) }} generated.
                                span(v-else)
                                    | Customer satisfies query parameters.

            //- Validate Potentials
            TimelineItem(
                v-if="validatedCustomersInProgress || validatedCustomersCompleted || validatedCustomersError"
                :inProgress="validatedCustomersInProgress"
                :completed="validatedCustomersCompleted"
                :error="validatedCustomersError"
                :errorMessage="enrollerRun.error_message"
                title="Validate Potentials"
                loadingText="Validating potential customers..."
            )
                template(v-slot:timeline-body)
                    div.event-container
                        v-card.event-summary(
                            elevation="0"
                        )
                            //- Campaign Simulation
                            template(v-if="isCampaignSimulation")
                                v-card-title.text-medium
                                    | {{ enrollerRun.total_enroller_run_customers_valid | number }} customer{{ handlePlural(enrollerRun.total_enroller_run_customers_valid) }} passed all validation criteria.
                                v-card-text(min-height="500px").d-flex.flex-column.justify-space-between
                                    .table-container
                                        TableFixedHeader(
                                            :items="validatedCustomers"
                                            hide-count
                                        )
                                            template(v-slot:th-default)
                                                th.no-wrap Customer
                                                th.no-wrap Valid
                                                th.no-wrap Customer Key
                                            template(v-slot:tr-default="slotProps")
                                                td.no-wrap {{ slotProps.item.first_name }} {{ slotProps.item.last_name }}
                                                td.no-wrap
                                                    v-icon.green--text(v-if="slotProps.item.is_valid") mdi-check
                                                    v-icon.red--text(v-else) mdi-close
                                                    v-menu(
                                                        open-on-hover
                                                        top
                                                        right
                                                        :min-width='800'
                                                        max-width='1000px'
                                                        max-height='1000px'
                                                        offset-y
                                                    )
                                                        template(v-slot:activator='{ on, attrs }')
                                                            v-icon.ml-2(color="primary" small v-on='on') mdi-information-outline
                                                        v-card(elevation="0")
                                                            CustomerValidationSummary(
                                                                :is-campaign-simulation="isCampaignSimulation"
                                                                :customer="slotProps.item"
                                                                :customer-flags="getCustomerFlags(slotProps.item.customer_key)"
                                                            )
                                                td.no-wrap 
                                                    router-link(:to="`/customers/${slotProps.item.customer_key}`" target="_blank") {{ slotProps.item.customer_key }} 
                                                    v-icon(color="primary" small right) mdi-open-in-new
                                        .fade-overlay(v-if="showSeeAllPotentials")
                                    v-btn(
                                        v-if="showSeeAllPotentials"
                                        color="primary"
                                        outlined
                                        append-icon="mdi-open-in-new"
                                        :to="{ name: 'enroller_runs_show', params: { vid: enrollerRun.vid, step: 3 } }"
                                        target="_blank"
                                    ) See All Potentials
                                        v-icon(color="primary" small right style="text-decoration: none;") mdi-open-in-new

                            //- Customer/Lead Simulation
                            template(v-else)
                                CustomerValidationSummary(
                                    :customer="validatedCustomers[0]"
                                    :customer-flags="getCustomerFlags(validatedCustomers[0].customer_key)"
                                    )

            //- Enroller Run Summary
            RunSummary(
                v-if="enrollerRunComplete || enrollerRunError"
                :enroller-run="enrollerRun"
                :enroller-run-complete="enrollerRunComplete"
                :enroller-run-error="enrollerRunError"
            )

</template>

<script lang="ts">
import Vue from 'vue';

// Components
import CustomerValidationSummary from '@/components/enroller_runs/simulator/customer_validation_summary.vue';
import RunSummary from '@/components/enroller_runs/simulator/run_summary.vue';
import TableFixedHeader from '@/components/shared/table_fixed_header.vue';
import TimelineItem from '@/components/enroller_runs/simulator/timeline_item.vue';

// Data
import { GetEnrollerRunCriterions, GetEnrollerRunCustomers } from '@/data';

export default Vue.extend({
    data() {
        return {
            enrollerRunCriterions: [],
            customerFlags: [],
            criterionsLoading: false,
            validatedCustomers: [],
            flags: [
                {
                    name: "Already in Queue",
                    key: "passed_already_in_queue_flag",
                    value: null,
                },
                {
                    name: 'CASL',
                    key: 'passed_casl_flag',
                    value: null,
                },
                {
                    name: 'Deleted',
                    key: 'passed_deleted_flag',
                    value: null,
                },
                {
                    name: 'Marketing Exclusion',
                    key: 'passed_marketing_exclusion_days_flag',
                    value: null,
                },
                {
                    name: 'Opted Out',
                    key: 'passed_opt_out_all_flag',
                    value: null,
                },
                {
                    name: 'Unique Event',
                    key: 'passed_unique_event_flag',
                    value: null,
                },
                {
                    name: 'Valid Email',
                    key: 'passed_valid_email_flag',
                    value: null,
                },
                {
                    name: 'Valid Message',
                    key: 'passed_valid_message_flag',
                    value: null,
                },
                {
                    name: 'Valid Phone',
                    key: 'passed_valid_phone_flag',
                    value: null,
                },
                {
                    name: 'Federal Do Not Call List',
                    key: 'passed_federal_dnc_flag',
                    value: null,
                },
            ],
            scrollObserver: null,
            limit: 8,
        }
    },
    methods: {
        getCustomerFlags(customerKey: string) {
            return this.customerFlags.find((customerFlag: any) => customerFlag.key === customerKey);
        },
        getEnrollerRunCriterions() {
            if (!this.validEnrollerRun) {
                return;
            }

            this.criterionsLoading = true;
            GetEnrollerRunCriterions(this.enrollerRun?.enroller_run_id).then((response) => {
                if (response?.Data) {
                    this.enrollerRunCriterions = response?.Data?.enroller_run_criterions;
                } else {
                    this.enrollerRunCriterions = [];
                }
                this.criterionsLoading = false;
            });
        },
        getEnrollerRunCustomers() {
            if (!this.validEnrollerRun) {
                return;
            }

            const options = {
                limit: this.limit || 8,
            }; 

            GetEnrollerRunCustomers(this.enrollerRun?.enroller_run_id, options).then((response) => {
                if (response?.Data) {
                        this.validatedCustomers = response?.Data?.enroller_run_customers;

                        //set flags for individual customer stats
                        this.validatedCustomers.forEach((customer: any) => {
                            // null check
                            if (!customer) {
                                return;
                            }
                            
                            const customerFlagData = {
                                key: customer.customer_key,
                                failedFlags: [],
                                notApplicableFlags: [],
                                passedFlags: [],
                            };

                            this.flags.forEach((flag: any) => {
                                flag.value = customer[flag.key];

                                if (flag.value === false) {
                                    customerFlagData.failedFlags.push(flag);
                                } else if (flag.value === null) {
                                    customerFlagData.notApplicableFlags.push(flag);
                                } else if (flag.value === true) {
                                    customerFlagData.passedFlags.push(flag);
                                }
                            });

                            this.customerFlags.push(customerFlagData);
                        });
                } else {
                    this.validatedCustomers = [];
                }
            });
        },
        handlePlural(num: number) {
            if (num === 1) {
                return '';
            }
            return 's';
        },
        queryParamsErrorMessage(currentErrorMessage: string) {
            if (currentErrorMessage === 'Zero potential customers found for criterion' && !this.isCampaignSimulation) {
                return 'Customer does not satisfy all query parameters.';
            }
            return currentErrorMessage;
        },
        setupScrollObserver(selector: string) {
            const container = document.querySelector(selector);
            if (!container) {
                return null;
            }
            
            const observer = new MutationObserver(() => {
                // scroll parent if the simulation-results-container is not scrollable - most common
                if (container.scrollHeight <= container.clientHeight) {
                    const parent = container.parentElement;
                    if (parent && parent.scrollHeight > parent.clientHeight) {
                        parent.scrollTop = parent.scrollHeight;
                        parent.scrollTo({
                            top: parent.scrollHeight,
                            behavior: 'smooth'
                        });
                    }
                } else {
                    // scroll container if it is scrollable - rare (if ever), but here just in case
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            });
            
            observer.observe(container, { 
                childList: true, 
                subtree: true,
            });

            return observer;            
        },
    },
    computed: {
        enrollerRunComplete() {
            return this.enrollerRun.status === 'completed';
        },
        enrollerRunError() {
            return this.enrollerRun.status === 'error';
        },
        isCampaignSimulation() {
            return this.enrollableType === 'Campaign';
        },
        isCustomerSimulation() {
            return this.enrollableType === 'Customer';
        },
        isProspectSimulation() {
            return this.enrollableType === 'Prospect';
        },
        potentialsError() {
            return this.enrollerRun.status_enroller_run_customers === 'error';
        },
        potentialsInProgress() {
            return this.enrollerRun.status_enroller_run_customers === 'scheduled' && this.enrollerRun.status_criterions === 'completed';
        },
        potentialsCompleted() {
            return this.enrollerRun.status_enroller_run_customers === 'completed';
        },
        queryParamsInProgress() {
            return this.enrollerRun.status_criterions === 'scheduled';
        },
        queryParamsCompleted() {
            return this.enrollerRun.status_criterions === 'completed' ;
        },
        queryParamsError() {
            return this.enrollerRun.status_criterions === 'error';
        },
        showSeeAllPotentials() {
            return this.isCampaignSimulation && this.enrollerRun.total_enroller_run_customers_valid > this.limit;
        },
        validatedCustomersInProgress() {
            return this.enrollerRun.status_customer_validation === 'scheduled' && this.enrollerRun.status_enroller_run_customers === 'completed';
        },
        validatedCustomersCompleted() {
            return this.enrollerRun.status_customer_validation === 'completed';
        },
        validatedCustomersError() {
            return this.enrollerRun.status_customer_validation === 'error';
        },
        validEnrollerRun() {
            return this.enrollerRun && this.enrollerRun?.enroller_run_id > 0;
        },
    },
    components: {
        CustomerValidationSummary,
        RunSummary,
        TableFixedHeader,
        TimelineItem,
    },
    props: {
        campaignName: String,
        customer: Object,
        date: String,
        time: String,
        enrollableType: String,
        enrollerRun: Object,
        formSubmitted: Boolean,
        isLoading: Boolean,
        organizationName: String,
        totalCriterions: Number,
    },
    watch: {
        'enrollerRun.status_criterions': {
            handler() {
                if (this.queryParamsCompleted || this.queryParamsError) {
                    this.getEnrollerRunCriterions();
                }
            },
        },
        'enrollerRun.status_customer_validation': {
            handler() {
                if (this.validatedCustomersCompleted) {
                    this.getEnrollerRunCustomers();
                }
            },
        },
        'enrollerRun.status': {
            handler() {
                if (this.enrollerRunComplete || this.enrollerRunError) {
                    // stop scroll to bottom on DOM updates since the run is finished
                    // small delay to ensure the summary renders first
                    setTimeout(() => {
                        if (this.scrollObserver) {
                            this.scrollObserver.disconnect();
                        }
                    }, 500);
                }
            },
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.scrollObserver = this.setupScrollObserver('.simulation-results-container');
        });
    },
    destroyed() {
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
        }
    },
})
</script>

<style lang="scss" scoped>
    .event-container {
        display: flex;
        flex-direction: column;
        border-radius: 10px;
        border: 1px solid var(--off-white);
        width: 100%;

        .event-summary:not(:last-of-type) {
            border-bottom: 1px solid var(--off-white);
        }
    }

    .table-container {
        position: relative;
        
        .fade-overlay {
            pointer-events: none;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 40px;
            background: linear-gradient(
                to bottom,
                rgba(255, 255, 255, 0) 0%,
                rgba(255, 255, 255, 0.3) 30%,
                rgba(255, 255, 255, 0.7) 60%,
                rgba(255, 255, 255, 1) 100%
            );
        }
    }
</style>